import React, { FC, useState, useEffect } from 'react';
import { FamilyConnectPrompt, UserProfile } from '../types';
import { translateContent } from '../lib/gemini';
import { LANGUAGES } from '../lib/constants';

export const FamilyConnectWidget = ({
    user,
    t,
    prompts,
    onGenerate
}: {
    user: UserProfile;
    t: (key: string) => string;
    prompts: FamilyConnectPrompt[];
    onGenerate: () => Promise<void>;
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const today = new Date().toISOString().split('T')[0];
    const todaysPrompt = prompts.find(p => p.date === today);

    const [translatedPrompt, setTranslatedPrompt] = useState<string | null>(null);
    const [translatedTip, setTranslatedTip] = useState<string | null>(null);
    const [showOriginal, setShowOriginal] = useState(false);
    const [isLoadingTranslation, setIsLoadingTranslation] = useState(false);

    useEffect(() => {
        setTranslatedPrompt(null);
        setTranslatedTip(null);
        setShowOriginal(false);
    }, [user.language, todaysPrompt?.id, todaysPrompt?.prompt]);

    const handleGenerate = async () => {
        setIsLoading(true);
        await onGenerate();
        setTranslatedPrompt(null);
        setTranslatedTip(null);
        setShowOriginal(false);
        setIsLoading(false);
    };

    const handleTranslate = async () => {
        if (!todaysPrompt) return;
        setIsLoadingTranslation(true);
        try {
            const promptPromise = translateContent(todaysPrompt.prompt, user.language);
            const tipPromise = todaysPrompt.parentTip ? translateContent(todaysPrompt.parentTip, user.language) : Promise.resolve('');
    
            const [promptResult, tipResult] = await Promise.all([promptPromise, tipPromise]);
            
            setTranslatedPrompt(promptResult);
            if (todaysPrompt.parentTip) {
                setTranslatedTip(tipResult);
            }
            setShowOriginal(false);
        } catch (e) {
            console.error("Translation failed", e);
        } finally {
            setIsLoadingTranslation(false);
        }
    };
    
    const handleToggleTranslation = () => {
        if (!translatedPrompt) {
            handleTranslate();
        } else {
            setShowOriginal(!showOriginal);
        }
    };

    const promptToShow = showOriginal ? todaysPrompt?.prompt : (translatedPrompt || todaysPrompt?.prompt);
    const tipToShow = showOriginal ? todaysPrompt?.parentTip : (translatedTip || todaysPrompt?.parentTip);

    const categoryIcons: Record<FamilyConnectPrompt['category'], string> = {
        EQ: 'self_improvement',
        Ethics: 'balance',
        Resilience: 'hiking',
        PlanetProtectors: 'eco',
        General: 'family_restroom'
    };

    const categoryColors: Record<FamilyConnectPrompt['category'], string> = {
        EQ: 'bg-blue-100 text-blue-600',
        Ethics: 'bg-purple-100 text-purple-600',
        Resilience: 'bg-green-100 text-green-600',
        PlanetProtectors: 'bg-teal-100 text-teal-600',
        General: 'bg-indigo-100 text-indigo-600'
    };


    return (
        <div className="bg-white rounded-2xl shadow-sm border p-4 mb-4">
            <h3 className="font-bold text-lg text-gray-800">{t('family_connect')}</h3>
            <p className="text-sm text-gray-500 mb-4">{t('family_connect_prompt')}</p>

            {todaysPrompt ? (
                <div className="bg-indigo-50 p-4 rounded-xl space-y-3">
                    <div className="flex items-center gap-2">
                        <div className={`p-2 rounded-full ${categoryColors[todaysPrompt.category]}`}>
                            <span className="material-icons-round !text-base">{categoryIcons[todaysPrompt.category]}</span>
                        </div>
                        <p className="text-xs font-bold uppercase tracking-wide text-indigo-600">{todaysPrompt.category}</p>
                    </div>

                    <p className="text-lg font-medium text-gray-800 leading-tight">{promptToShow}</p>

                    {tipToShow && (
                        <div className="text-xs text-indigo-800 bg-indigo-100 p-2 rounded-lg">
                           <span className="font-bold">{t('parent_tip')}</span> {tipToShow}
                        </div>
                    )}
                    <button onClick={handleToggleTranslation} disabled={isLoadingTranslation} className="text-xs text-indigo-600 hover:text-indigo-800 font-bold mt-2 flex items-center gap-1">
                        <span className="material-icons-round text-[13px]">translate</span>
                        {isLoadingTranslation ? (t('processing_translation') || 'Translating...') : (showOriginal ? (t('see_translation') || 'See Translation') : (translatedPrompt ? (t('show_original') || 'Show Original') : (t('translate') || 'Translate')))}
                    </button>
                </div>
            ) : (
                <button
                    onClick={handleGenerate}
                    disabled={isLoading}
                    className="w-full py-3 bg-indigo-600 text-white font-bold rounded-xl disabled:opacity-50 flex items-center justify-center"
                >
                     {isLoading ? <span className="material-icons-round animate-spin">sync</span> : t('generate_new_idea')}
                </button>
            )}
             <button
                onClick={handleGenerate}
                disabled={isLoading}
                className="w-full text-center text-sm text-gray-500 py-2 mt-2 flex items-center justify-center gap-1 disabled:opacity-50"
            >
                <span className="material-icons-round text-base">shuffle</span>
                {isLoading ? t('generating') : t('shuffle_idea')}
            </button>
        </div>
    );
};

// FIX: Use React.FC to correctly type the component and allow the 'key' prop.
const CompassPromptItem: FC<{ prompt: FamilyConnectPrompt, user: UserProfile, t: (key: string) => string }> = ({ prompt, user, t }) => {
    const [translatedPrompt, setTranslatedPrompt] = useState<string | null>(null);
    const [showOriginal, setShowOriginal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        setTranslatedPrompt(null);
        setShowOriginal(false);
    }, [user.language, prompt.id, prompt.prompt]);

    const handleTranslate = async () => {
        setIsLoading(true);
        try {
            const res = await translateContent(prompt.prompt, user.language);
            setTranslatedPrompt(res);
            setShowOriginal(false);
        } catch (e) {
            console.error("Translation failed", e);
        } finally {
            setIsLoading(false);
        }
    };

    const handleToggle = () => {
        if (!translatedPrompt) {
            handleTranslate();
        } else {
            setShowOriginal(!showOriginal);
        }
    };

    const textToShow = showOriginal ? prompt.prompt : (translatedPrompt || prompt.prompt);
    
    const categoryDetails: Record<FamilyConnectPrompt['category'], { icon: string, color: string, name: string }> = {
        EQ: { icon: 'self_improvement', color: 'text-blue-500', name: 'Emotional Quotient' },
        Resilience: { icon: 'hiking', color: 'text-green-500', name: 'Resilience' },
        Ethics: { icon: 'balance', color: 'text-purple-500', name: 'Ethics' },
        PlanetProtectors: { icon: 'eco', color: 'text-teal-500', name: 'Planet Protectors' },
        General: { icon: 'family_restroom', color: 'text-indigo-500', name: 'General' },
    };

    return (
        <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
                <span className={`material-icons-round !text-base ${categoryDetails[prompt.category].color}`}>{categoryDetails[prompt.category].icon}</span>
                <p className="text-xs font-bold uppercase tracking-wide">{categoryDetails[prompt.category].name}</p>
            </div>
            <p className="text-sm text-gray-800">{textToShow}</p>
            <button onClick={handleToggle} disabled={isLoading} className="text-xs text-indigo-600 hover:text-indigo-800 font-bold mt-2 flex items-center gap-1">
                <span className="material-icons-round text-[12px]">translate</span>
                {isLoading ? (t('processing_translation') || 'Translating...') : (showOriginal ? (t('see_translation') || 'See Translation') : (translatedPrompt ? (t('show_original') || 'Show Original') : (t('translate') || 'Translate')))}
            </button>
            <p className="text-xs text-gray-400 mt-1">{new Date(prompt.date).toLocaleDateString()}</p>
        </div>
    );
};

export const CharacterCompassView = ({ prompts, t, showCheckinNudge, onNudgeClick, user }: { 
    prompts: FamilyConnectPrompt[], 
    user: UserProfile,
    t: (key: string, reps?: any) => string,
    showCheckinNudge: boolean,
    onNudgeClick: () => void,
}) => {
    
    const categories: FamilyConnectPrompt['category'][] = ['EQ', 'Resilience', 'Ethics', 'PlanetProtectors'];
    
    const categoryDetails: Record<FamilyConnectPrompt['category'], { icon: string, color: string, name: string }> = {
        EQ: { icon: 'self_improvement', color: 'text-blue-500', name: 'Emotional Quotient' },
        Resilience: { icon: 'hiking', color: 'text-green-500', name: 'Resilience' },
        Ethics: { icon: 'balance', color: 'text-purple-500', name: 'Ethics' },
        PlanetProtectors: { icon: 'eco', color: 'text-teal-500', name: 'Planet Protectors' },
        General: { icon: 'family_restroom', color: 'text-indigo-500', name: 'General' },
    };

    const promptsByCategory = prompts.reduce((acc, prompt) => {
        if (!acc[prompt.category]) {
            acc[prompt.category] = [];
        }
        acc[prompt.category].push(prompt);
        return acc;
    }, {} as Record<FamilyConnectPrompt['category'], FamilyConnectPrompt[]>);

    const totalPrompts = prompts.length;

    return (
        <div className="p-4 space-y-4">
            {showCheckinNudge && (
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
                    <div className="flex items-start gap-3">
                        <span className="material-icons-round text-blue-500 mt-1">auto_awesome</span>
                        <div>
                            <h4 className="font-bold text-blue-800">{t('unlock_insights')}</h4>
                            <p className="text-sm text-blue-700 mt-1">{t('unlock_insights_prompt')}</p>
                            <button onClick={onNudgeClick} className="mt-3 bg-blue-500 text-white text-sm font-bold py-2 px-4 rounded-lg hover:bg-blue-600">
                                {t('submit_weekly_checkin')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {totalPrompts === 0 && !showCheckinNudge && (
                <div className="p-8 text-center text-gray-500">
                    <span className="material-icons-round text-5xl opacity-50 mb-4">explore</span>
                    <h3 className="font-bold text-lg">{t('no_prompts_yet')}</h3>
                </div>
            )}

            {totalPrompts > 0 && (
                <>
                    <div className="bg-white rounded-xl shadow-sm border p-4">
                        <h3 className="font-bold text-lg">{t('character_compass')}</h3>
                        <p className="text-sm text-gray-500 mb-4">{t('compass_intro')}</p>
                        <div className="grid grid-cols-2 gap-4">
                            {categories.map(cat => {
                                const count = promptsByCategory[cat]?.length || 0;
                                const percentage = totalPrompts > 0 ? (count / totalPrompts) * 100 : 0;
                                return (
                                    <div key={cat} className="bg-gray-50 p-3 rounded-lg flex items-start gap-3">
                                        <span className={`material-icons-round ${categoryDetails[cat].color}`}>{categoryDetails[cat].icon}</span>
                                        <div>
                                            <p className="font-bold text-sm">{categoryDetails[cat].name}</p>
                                            <p className="text-xs text-gray-500">{count} conversations</p>
                                            <div className="h-1.5 w-full bg-gray-200 rounded-full mt-2">
                                                <div className={`h-1.5 rounded-full ${categoryDetails[cat].color.replace('text-', 'bg-')}`} style={{ width: `${percentage}%` }}></div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border p-4">
                        <h3 className="font-bold text-lg">{t('recent_conversations')}</h3>
                        <div className="space-y-3 mt-2">
                           {prompts.slice(0, 5).map(prompt => (
                                <CompassPromptItem key={prompt.id} prompt={prompt} user={user} t={t} />
                           ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};